import {
  CSSProperties,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import styles from './index.scss'
import { CenterPopup } from 'antd-mobile'
import { CloseCircleOutline } from 'antd-mobile-icons'
import useMoreNotice from './useMoreNotice'
import classNames from 'classnames'
import { TrackView } from '@dragonpass/intl-unified-tracker'
import DpTracker from '@/libs/components/DpTrackerComponents/DpTracker'

type NoticeLayoutChildren =
  | ReactNode
  | ((params: {
      isShowRollNotice: boolean
      noticeModalVisible: boolean
    }) => ReactNode)

interface IProps {
  children: NoticeLayoutChildren
}

const Marquee = ({
  text,
  speed,
  style,
}: {
  text: string
  speed: number
  style?: CSSProperties
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const textRef = useRef<HTMLSpanElement | null>(null)
  const [duration, setDuration] = useState(12)
  const [shift, setShift] = useState(0)
  const [startX, setStartX] = useState(0)
  const [gap, setGap] = useState(0)

  useLayoutEffect(() => {
    const el = textRef.current
    const container = containerRef.current
    if (!el || !container) return

    const textWidth = el.scrollWidth
    const containerWidth = container.clientWidth || window.innerWidth || 0
    const viewportWidth = window.innerWidth || containerWidth
    const nextGap = Math.max(containerWidth, viewportWidth)
    const pxPerSecond = 1000 / Math.max(speed, 1)
    const distance = textWidth + nextGap
    const nextDuration = Math.max(distance / pxPerSecond, 3)

    setGap(nextGap)
    setStartX(containerWidth)
    setShift(distance)
    setDuration(nextDuration)
  }, [text, speed])

  const marqueeStyle = {
    '--marquee-duration': `${duration}s`,
    '--marquee-start': `${startX}px`,
    '--marquee-gap': `${gap}px`,
    '--marquee-shift': `${shift}px`,
  } as CSSProperties

  return (
    <div className={styles.marqueeWrapper} style={style}>
      <img
        src="https://oss-images.dragonpass.com.cn/business-saas/platform/2024-08-30_172651/sound.png"
        alt="Sound Icon"
        className={styles.marqueeSoundIcon}
      />
      <div ref={containerRef} className={styles.marqueeContainer}>
        <div className={styles.marqueeTrack} style={marqueeStyle}>
          <span ref={textRef} className={styles.marqueeText}>
            {text}
          </span>
          <span className={styles.marqueeText} aria-hidden={true}>
            {text}
          </span>
        </div>
      </div>
    </div>
  )
}

function NoticeModal(props: {
  onOk(): void
  onCancel(): void
  visible: boolean
  style: object
  title: string
  content: string
  type: 'poster' | 'popup'
  jumpUrl?: string
  openReadCountdown: boolean
  countdownSecond: number
}) {
  const [isOnCountDown, setIsOnCountDown] = useState(false)
  const [second, setSecond] = useState(0)
  const {
    onCancel,
    visible,
    style,
    onOk = () => {},
    title,
    content,
    type,
    jumpUrl = '',
    openReadCountdown = false,
    countdownSecond = 0,
  } = props

  useEffect(() => {
    if (!visible) {
      setIsOnCountDown(false)
      return
    }
    if (!openReadCountdown || !countdownSecond) {
      setIsOnCountDown(false)
      return
    }

    setSecond(countdownSecond)
    setIsOnCountDown(true)

    let current = countdownSecond
    const timer = setInterval(() => {
      current -= 1
      setSecond(current)
      if (current <= 0) {
        clearInterval(timer)
        setIsOnCountDown(false)
      }
    }, 1000)

    return () => {
      clearInterval(timer)
    }
  }, [visible, openReadCountdown, countdownSecond])

  const handleCancel = () => {
    if (isOnCountDown) return
    window?.dpTracker?.sendClk({
      eventData: {
        module_name: '通知公告',
        component_name: '关闭按钮',
      },
    })
    onCancel && onCancel()
  }

  const bottomBtnCls = classNames(styles.bottomBtn, {
    [styles.bottomBtnDisabled]: isOnCountDown, // 处于倒计时就禁用
  })

  return (
    <CenterPopup
      style={{
        padding: 0,
        '--max-width': '100vw',
        ...style,
      }}
      className={styles.noticeModal}
      visible={visible}
      bodyStyle={{ background: 'transparent' }}
      onClose={handleCancel}
      onMaskClick={handleCancel}
    >
      {type === 'popup' ? (
        <div className={styles.noticeModalContainer}>
          <img
            className={styles.soundIcon}
            src="https://oss-images.dragonpass.com.cn/business-saas/platform/2024-09-02_134753/sound-big.png"
            alt=""
          />
          <div className={styles.noticeModalBody}>
            <TrackView
              component="div"
              trackClk={false}
              trackData={{
                module_name: '通知公告',
              }}
              trackExp={true}
            >
              <div className={styles.title}>{title || '重要通知'}</div>
            </TrackView>

            <div
              className={styles.content}
              dangerouslySetInnerHTML={{ __html: content }}
            ></div>
            <div className={styles.bottomBtnWrap}>
              <div
                onClick={() => {
                  if (isOnCountDown) return
                  onOk && onOk()
                }}
                className={bottomBtnCls}
              >
                {isOnCountDown ? `已阅读(${second}S)` : '已阅读'}
              </div>
            </div>
          </div>
          <CloseCircleOutline
            onClick={handleCancel}
            fontSize={28}
            className={styles.closeBtn}
          />
        </div>
      ) : (
        <div
          className={`${styles.noticeModalContainer} ${styles.noticeModalContainerPoster}`}
        >
          <img
            onClick={() => {
              onOk && onOk()
            }}
            className={styles.poster}
            src={content}
            alt={title}
          />
          <CloseCircleOutline
            onClick={handleCancel}
            fontSize={28}
            className={styles.closeBtn}
          />
        </div>
      )}
    </CenterPopup>
  )
}

interface IProps {
  sectionCode: string
  isReadyToShowNotice: boolean
  style: Object
  marqueeStyle?: CSSProperties
}

export default function noticeListLayout(props: IProps) {
  const {
    sectionCode = '',
    isReadyToShowNotice = false,
    style = {},
    marqueeStyle,
  } = props
  const [noticeModalVisible, setNoticeModalVisible] = useState(true)
  // const { noticeInfo, setData, visible, onReadNotice } = useMoreNotice()
  const {
    noticeInfo,
    setData,
    onReadPopupNotice,
    noticePopupVisible,
    noticeRollVisible,
  } = useMoreNotice()
  const { rollNotice, popupOrPosterNotice } = noticeInfo

  useEffect(() => {
    if (!sectionCode) return
    setNoticeModalVisible(true)
    setData(sectionCode)
  }, [sectionCode])

  return (
    <DpTracker>
      {isReadyToShowNotice && noticeRollVisible && rollNotice && (
        <Marquee
          text={(rollNotice.title || '') + (rollNotice.content || '')}
          speed={10}
          style={marqueeStyle}
        />
      )}
      {typeof props.children === 'function'
        ? props.children({
            isShowRollNotice: !!(noticeRollVisible && rollNotice),
            noticeModalVisible: noticeModalVisible && noticePopupVisible,
          })
        : props.children}
      {isReadyToShowNotice && noticePopupVisible && popupOrPosterNotice && (
        <NoticeModal
          type={popupOrPosterNotice.type}
          title={popupOrPosterNotice.title || ''}
          content={popupOrPosterNotice.content || ''}
          style={style}
          onOk={() => {
            const { type, jumpUrl, contentMd5, popupCount } =
              popupOrPosterNotice
            window?.dpTracker?.sendClk({
              eventData: {
                module_name: '通知公告',
                component_name: '已阅读',
              },
            })
            if (popupCount === 'infinite') {
              setNoticeModalVisible(false)
            } else {
              if (contentMd5) {
                onReadPopupNotice(contentMd5)
              }
            }
            if (type === 'poster' && jumpUrl) {
              window.location.href = jumpUrl
            }
          }}
          openReadCountdown={popupOrPosterNotice.openReadCountdown || false}
          countdownSecond={popupOrPosterNotice.countdownSecond || 0}
          visible={noticeModalVisible}
          onCancel={() => setNoticeModalVisible(false)}
        />
      )}
    </DpTracker>
  )
}
